import React, { useState, useEffect, useCallback } from 'react';
import { startOfDay } from 'date-fns';
import { swap } from 'util/ordering-util';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AppStoreState, LESSON_PLANNER_VIEW, LESSON_PLANNER_MODE } from 'types';
import { LessonPlannerFooter } from '.';
import { WithDragDrop } from 'components/@layout/WithDragDrop';
import { setLessonPlanNumberOfWeeks } from 'state/modules/lessonPlanner';
import {
  incrementLessonPlanWeek,
  decramentLessonPlanWeek,
  incrementLessonPlanDay,
  decramentLessonPlanDay,
  setLessonPlanWeek,
  setLessonPlanToDate
} from 'state/modules/lessonPlanner/thunks';
import { fillDayGaps, getUserHasTeachingOrder, getWeekStartingIndex } from 'util/lesson-plan-util';
import { getNewLink } from 'util/workbook-util';
import { getUserAssignmentsView } from 'state/modules/entity/thunks/assignment';
import { ViewAssignments } from 'containers/LessonAssignment/components/ViewAssignments';
import { LessonPlannerMain } from './LessonPlannerMain';
import { buildSearchQueryParams } from 'util/query-params-util';
import { AssignmentEngineParams } from 'state/providers/LessonAssignmentProvider/types';
import {
  formatFullDate,
  getEpochTime,
  getWeekday,
  getStartOfWeek,
  isSameDay
} from 'util/date-util';
import { LessonPlannerTour } from '../../Tours/modules/LessonPlanner/LessonPlannerTour';
import SelectStartDateModal, {
  SelectStartDateModalBackdrop
} from '../../../components/@common/Modals/SelectStartDateModal';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { ManageTeachingOrder } from 'containers/ManageOptions';
import { translate } from 'translations/translator';

const getTranslation = (key, options?) => translate('lessonPlanner', key, options);

export const LessonPlannerView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch() as any;
  const [, { openModal }] = useModal();
  const { bookCode, edition } = useParams<any>();
  const [{ lessons }, setLessonPlan] = useState<any>({ lessons: null });
  const currentlessonPlanWeek = useSelector(
    ({ lessonPlanner }: AppStoreState) => lessonPlanner.currentlessonPlanWeek
  );
  const { view: selectedView } = useSelector(
    ({ lessonPlanner }: AppStoreState) => lessonPlanner,
    shallowEqual
  );
  const user = useSelector(({ user }: any) => user);

  const lessonPlannerTour = user.settings?.tours?.find(({ name }) => name === 'lessonPlanner');
  const shouldDoLessonPlannerTour = !!lessonPlannerTour?.steps?.length;

  const lessonPlan: any = useSelector(({ entity }: AppStoreState) => entity.lessonPlan);
  const currentWorkbook = useSelector(({ app }: AppStoreState) => app.currentWorkbook);
  const locale = useSelector(({ app }: AppStoreState) => app.locale);

  const [{ productCurriculumId: selectedProductCurriculumId }] = currentWorkbook?.products ?? [{}];

  const userHasTeachingOrder = getUserHasTeachingOrder(user, selectedProductCurriculumId);

  const assignmentsOnlyData: any = useSelector(
    ({ entity }: AppStoreState) => entity.userAssignments
  );
  const { mode }: any = useSelector((state: any) => state.lessonPlanner, shallowEqual);
  const handleNextClick = () => {
    if (selectedView === LESSON_PLANNER_VIEW.DAY) {
      dispatch(incrementLessonPlanDay());
      return;
    }
    dispatch(incrementLessonPlanWeek());
  };
  const handlePreviousClick = () => {
    if (selectedView === LESSON_PLANNER_VIEW.DAY) {
      dispatch(decramentLessonPlanDay());
      return;
    }
    dispatch(decramentLessonPlanWeek());
  };

  const handleGoToTodayClick = () =>
    selectedView === LESSON_PLANNER_VIEW.DAY
      ? setLessonPlanWeekToToday()
      : setLessonPlanWeekToThisWeek();

  const setLessonPlanWeekToThisWeek = useCallback(() => {
    const monday = formatFullDate(getStartOfWeek());
    const index = lessons.findIndex(({ lessonDate, date }) =>
      isSameDay(lessonDate || date, monday)
    );
    if (index >= 0) {
      dispatch(setLessonPlanWeek(Math.ceil(index / 5) + 1));
    } else {
      dispatch(setLessonPlanWeek(1));
    }
  }, [dispatch, lessons]);

  const setLessonPlanWeekToToday = useCallback(() => {
    const weekday = formatFullDate(getWeekday());
    const index = lessons.findIndex(({ lessonDate, date }) =>
      isSameDay(lessonDate || date, weekday)
    );
    if (index >= 0) {
      dispatch(setLessonPlanToDate(Math.ceil(index / 5), index % 5));
    } else {
      dispatch(setLessonPlanToDate(1, 0));
    }
  }, [dispatch, lessons]);

  useEffect(() => {
    if (lessons) {
      dispatch(setLessonPlanNumberOfWeeks(lessons.length / 5));
      if (currentlessonPlanWeek < 1) {
        setLessonPlanWeekToThisWeek();
      }
    }
  }, [lessons, currentlessonPlanWeek, setLessonPlanWeekToThisWeek, dispatch]);

  useEffect(() => {
    if (mode === LESSON_PLANNER_MODE.ASSIGNMENTS) {
      if (!assignmentsOnlyData) dispatch(getUserAssignmentsView());

      setLessonPlan({ ...lessonPlan, lessons: fillDayGaps(assignmentsOnlyData, locale) });
    }
  }, [mode, assignmentsOnlyData, dispatch, lessonPlan, locale]);

  useEffect(() => {
    if (lessonPlan && mode === LESSON_PLANNER_MODE.LESSON_PLANNER) {
      setLessonPlan(lessonPlan);
    }
  }, [lessonPlan, mode]);

  // watch for no 'teachingOrder' setting and show modal
  useEffect(() => {
    if (!shouldDoLessonPlannerTour && !userHasTeachingOrder && !user.isAuditor) {
      openModal({
        body: <SelectStartDateModal />,
        options: {
          closeOnBackdropClick: false,
          Backdrop: (<SelectStartDateModalBackdrop />) as any
        }
      });
    }
  }, [openModal, shouldDoLessonPlannerTour, userHasTeachingOrder, user.isAuditor]);

  const handleBlankAssignment = () => {
    const [{ productCurriculumId }] = currentWorkbook?.products ?? [{}];
    const epochTime = getEpochTime(startOfDay(getWeekday()) as any);

    navigate({
      pathname: `/lesson-assignment/${bookCode}/${edition}/${productCurriculumId}/${epochTime}`,
      search: buildSearchQueryParams({ assignment: AssignmentEngineParams.BLANK })
    });
  };

  const handleStudentProgress = () => {
    navigate(getNewLink(bookCode ?? '', edition ?? '', 'assignmentTracker'));
  };

  const onMoveCard = (currentIndex: number, dragIndex: number) => {
    setLessonPlan(swap(lessons, currentIndex, dragIndex));
  };

  const gridProps = {
    xs: 12
  };

  const getLessons = () => {
    const start = getWeekStartingIndex(currentlessonPlanWeek);
    return lessons.slice(start, start + 5);
  };

  const getContent = () => {
    const getRenderConditions = (lessonPlan && lessons) || location.pathname.includes('/view');

    if (!getRenderConditions) return <div style={{ minHeight: '100%', height: '100%' }} />;

    if (location.pathname.includes('manage-options')) return <ManageTeachingOrder />;
    if (location.pathname.includes('view')) return <ViewAssignments />;
    if (shouldDoLessonPlannerTour) return <LessonPlannerTour />;

    return (
      <div style={{ minHeight: '100%', height: '100%' }}>
        <WithDragDrop>
          <LessonPlannerMain
            lessons={getLessons()}
            selectedView={selectedView}
            onMoveCard={onMoveCard}
            gridProps={gridProps}
            title={lessonPlan.name}
            lessonWeek={getTranslation('week', { weekNumber: currentlessonPlanWeek })}
            handleNextClick={handleNextClick}
            handlePreviousClick={handlePreviousClick}
            handleGoToTodayClick={handleGoToTodayClick}
          />
        </WithDragDrop>
      </div>
    );
  };

  return (
    <>
      {getContent()}
      <LessonPlannerFooter
        handleBlankAssignment={handleBlankAssignment}
        handleStudentProgress={handleStudentProgress}
      />
    </>
  );
};
